import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { onboardingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, GraduationCap, CheckCircle2, Zap, BookOpen, Award } from 'lucide-react';
import { toast } from 'sonner';

const ValnexoLogo = ({ size = 50 }) => (
    <img
        src="/valnexo-logo-official-new.png"
        alt="Valnexo Logo"
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: 6 }}
    />
);

export default function OnboardingSignup() {
    const navigate = useNavigate();
    const { setAuthData } = useAuth();

    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [token, setToken] = useState(null);
    const [entering, setEntering] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        setLoading(true);
        try {
            // On crée le compte mais on NE connecte PAS encore : on affiche d'abord
            // l'écran de confirmation avec le bouton « Accéder à l'Académie ».
            const res = await onboardingAPI.register({
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                password: form.password,
            });
            setToken(res.data.access_token);
            setDone(true);
            toast.success('Compte créé ! Bienvenue dans votre formation.');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Erreur lors de la création du compte');
        } finally {
            setLoading(false);
        }
    };

    const enterAcademy = async () => {
        setEntering(true);
        try {
            await setAuthData(token); // ouvre la session → la redirection vers /academy se fait ensuite
            navigate('/academy');
        } catch (e) {
            toast.error('Session expirée, veuillez vous connecter.');
            navigate('/login');
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left — branding */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0B1F3B' }}>
                <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(135deg, rgba(11,31,59,0.95) 0%, rgba(11,31,59,0.85) 50%, rgba(124,58,237,0.35) 100%)' }}
                />
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#7c3aed' }} />
                    <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#F2B705' }} />
                </div>
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div className="flex items-center gap-4">
                        <ValnexoLogo size={55} />
                        <div>
                            <h1 className="font-bold text-2xl tracking-wide" style={{ color: '#F5F7FA' }}>VALNEXO</h1>
                            <p className="text-sm tracking-widest font-medium" style={{ color: '#7c3aed' }}>ACADÉMIE</p>
                        </div>
                    </div>
                    <div className="space-y-8">
                        <h2 className="text-4xl font-bold leading-tight" style={{ color: '#F5F7FA' }}>
                            Rejoignez le réseau,
                            <br />
                            <span style={{ color: '#F2B705' }}>commencez votre formation.</span>
                        </h2>
                        <p className="text-lg opacity-90 max-w-md leading-relaxed" style={{ color: '#F5F7FA' }}>
                            Créez votre accès formation, suivez l'Académie Valnexo à votre rythme,
                            puis devenez apporteur d'affaires actif.
                        </p>
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="flex items-center gap-3">
                                <BookOpen className="w-5 h-5" style={{ color: '#F2B705' }} />
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Modules de formation guidés</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Zap className="w-5 h-5" style={{ color: '#7c3aed' }} />
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Courtage Énergie & CEE</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Award className="w-5 h-5" style={{ color: '#22C55E' }} />
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Validation puis activation CRM</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-sm opacity-60" style={{ color: '#F5F7FA' }}>© 2026 Valnexo. Tous droits réservés.</div>
                </div>
            </div>

            {/* Right — form / success */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md space-y-8">
                    <div className="flex items-center gap-3 lg:hidden">
                        <ValnexoLogo size={45} />
                        <div>
                            <h1 className="font-bold text-xl" style={{ color: '#0B1F3B' }}>VALNEXO</h1>
                            <p className="text-xs tracking-widest" style={{ color: '#7c3aed' }}>ACADÉMIE</p>
                        </div>
                    </div>

                    {done ? (
                        <div className="text-center space-y-6" data-testid="onboarding-success">
                            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold" style={{ color: '#0B1F3B' }}>Compte créé !</h2>
                                <p className="mt-2 text-slate-600">
                                    Votre accès formation est prêt. Suivez les modules de l'Académie, puis demandez votre validation.
                                </p>
                            </div>
                            <Button
                                onClick={enterAcademy}
                                disabled={entering}
                                className="w-full h-12 text-white font-semibold hover:opacity-90"
                                style={{ backgroundColor: '#7c3aed' }}
                                data-testid="go-to-academy-btn"
                            >
                                {entering ? <Loader2 className="w-5 h-5 animate-spin" /> : <><GraduationCap className="w-5 h-5 mr-2" /> Accéder à l'Académie</>}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#0B1F3B' }}>Créer mon accès formation</h2>
                                <p className="mt-2 text-slate-600">Quelques informations pour démarrer l'Académie Valnexo.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="first_name" className="text-slate-700">Prénom</Label>
                                        <Input id="first_name" value={form.first_name} onChange={update('first_name')} required
                                            className="h-12 border-slate-200" data-testid="onboarding-firstname-input" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="last_name" className="text-slate-700">Nom</Label>
                                        <Input id="last_name" value={form.last_name} onChange={update('last_name')} required
                                            className="h-12 border-slate-200" data-testid="onboarding-lastname-input" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                                    <Input id="email" type="email" placeholder="votre@email.com" value={form.email} onChange={update('email')} required
                                        className="h-12 border-slate-200" data-testid="onboarding-email-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-slate-700">Téléphone</Label>
                                    <Input id="phone" type="tel" placeholder="06 00 00 00 00" value={form.phone} onChange={update('phone')}
                                        className="h-12 border-slate-200" data-testid="onboarding-phone-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                                    <div className="relative">
                                        <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                                            value={form.password} onChange={update('password')} required minLength={6}
                                            className="h-12 border-slate-200 pr-12" data-testid="onboarding-password-input" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400">Au moins 6 caractères.</p>
                                </div>

                                <Button type="submit" disabled={loading}
                                    className="w-full h-12 text-white font-semibold hover:opacity-90"
                                    style={{ backgroundColor: '#7c3aed' }}
                                    data-testid="onboarding-submit-btn">
                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Créer mon accès formation'}
                                </Button>
                            </form>

                            <p className="text-center text-sm text-slate-500">
                                Vous avez déjà un compte ?{' '}
                                <a href="/login" className="text-purple-600 hover:underline" data-testid="onboarding-login-link">Se connecter</a>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
